import { useEffect, useMemo, useRef, useState } from 'react';
import { useMessageValidation, ChannelType } from './useMessageValidation';
import { useMessageLength } from './useMessageLength';
import { KakaoButton } from '../Components/Common/ButtonsEditor';

export interface BulkFormState {
  channel: ChannelType;
  site_id?: number;
  template_id?: number;
  message: string;
  buttons: KakaoButton[];
  image?: { url: string; is_wide?: boolean } | null;
  fall_back_message?: string;
  recipientsCount: number;
  maxRecipients: number;
}

export interface BulkFormValidationResult {
  valid: boolean;
  errors: Record<string,string>;
  bytes: number;
  classification: 'SM' | 'LM' | 'OVER';
  effectiveChannel: ChannelType; // After auto-class (SM -> LM) if needed
  canSend: boolean; // valid + recipients + not OVER
  changedChannel: boolean; // true if effectiveChannel differs from given channel
  revalidate: () => Promise<void>;
}

export function useBulkFormValidation(state: BulkFormState): BulkFormValidationResult {
  const { validate } = useMessageValidation();
  const { bytes, classification, overLimit } = useMessageLength(state.message);
  // Auto-classify SMS -> LMS for bytes > 90
  const effectiveChannel: ChannelType = useMemo(() => {
    if (state.channel === 'SM' && classification === 'LM') return 'LM';
    return state.channel;
  }, [state.channel, classification]);

  const [valid, setValid] = useState(false);
  const [errors, setErrors] = useState<Record<string,string>>({});

  // Debounce validation to avoid rapid consecutive executions (e.g., large text or image state updates)
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);
  const runValidation = async () => {
    const doValidate = async () => {
      const result = await validate({
        channel: effectiveChannel,
        site_id: state.site_id,
        template_id: state.template_id,
        message: state.message,
        buttons: state.buttons,
        image: state.image,
        recipientsCount: state.recipientsCount,
        maxRecipients: state.maxRecipients,
        fall_back_message: state.fall_back_message,
      } as any);
      setValid(result.valid && !overLimit);
      setErrors(result.errors || {});
    };
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    // 120ms debounce window; fast enough for UX while eliminating bursts
    debounceTimer.current = setTimeout(doValidate, 120);
  };

  useEffect(() => {
    runValidation();
    // Cleanup on unmount
    return () => { if (debounceTimer.current) clearTimeout(debounceTimer.current); };
  }, [effectiveChannel, state.site_id, state.template_id, state.message, state.buttons, state.image, state.recipientsCount, state.maxRecipients, state.fall_back_message, overLimit]);

  const canSend = valid && state.recipientsCount > 0 && !overLimit;

  return {
    valid,
    errors,
    bytes,
    classification,
    effectiveChannel,
    canSend,
    changedChannel: effectiveChannel !== state.channel,
    revalidate: runValidation,
  };
}

export default useBulkFormValidation;
